import { useEffect, type CSSProperties, type ReactNode } from "react";
import { useScrollLock } from "../lib/useScrollLock";

/**
 * Plain modal shell following the portal dialog pattern (§5.7 of the design
 * system): overlay click and Escape close it, the panel stops propagation.
 * Content, form and buttons are the caller's — this only owns the chrome.
 */
export function Modal({
  title,
  subtitle,
  onClose,
  children,
  width = 520,
}: {
  title: string;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}) {
  useScrollLock();
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div role="dialog" aria-modal="true" aria-label={title} className="modal-overlay" style={overlay} onClick={onClose}>
      <div className="modal-panel" style={{ ...panel, maxWidth: width }} onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">{title}</h2>
        {subtitle && <p className="modal-sub">{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(11, 58, 111, 0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 50,
  padding: 16,
};
const panel: CSSProperties = {
  width: "100%",
  // Tall dialogs (map picker, long forms) scroll inside the panel instead of overflowing the viewport.
  maxHeight: "calc(100vh - 32px)",
  overflowY: "auto",
  overscrollBehavior: "contain",
  background: "var(--surface)",
  padding: 24,
  borderRadius: 12,
  boxShadow: "var(--shadow-2)",
  display: "flex",
  flexDirection: "column",
  gap: 14,
};
